import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Grid2 as Grid,
} from "@mui/material";

import ReCAPTCHA from "react-google-recaptcha";
import { ParticipantData } from "../models/Participant";

interface RegistrationFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => void;
  registrationCode?: string;
  selectedCourseName?: string;
  participantData?: ParticipantData;
  loading?: boolean;
}

interface FormErrors {
  email?: string;
  confirmEmail?: string;
  firstName?: string;
  lastName?: string;
  positionTitle?: string;
  organization?: string;
  address?: string;
  phoneNumber?: string;
}

const captchaKey = process.env.REACT_APP_CAPTCHA_SITE_KEY || "";

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  open,
  onClose,
  onSubmit,
  registrationCode,
  selectedCourseName,
  participantData,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    email: "",
    confirmEmail: "",
    firstName: "",
    lastName: "",
    positionTitle: "",
    organization: "",
    address: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  useEffect(() => {
    if (participantData) {
      setFormData({
        email: participantData.email,
        confirmEmail: participantData.email,
        firstName: participantData.firstName,
        lastName: participantData.lastName,
        positionTitle: participantData.positionTitle,
        organization: participantData.organization,
        address: participantData.address,
        phoneNumber: participantData.phoneNumber,
      });
    }
  }, [participantData]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (formData.confirmEmail !== formData.email) {
      newErrors.confirmEmail = "Emails do not match";
    }

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.positionTitle.trim())
      newErrors.positionTitle = "Position/Title is required";
    if (!formData.organization.trim())
      newErrors.organization = "Organization is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";

    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!phoneRegex.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Invalid phone number format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || loading) return;
    if (validateForm()) {
      setIsSubmitting(true);
      const submissionData = {
        ...formData,
        registrationCode,
      };
      Promise.resolve(onSubmit({ submissionData })).finally(() => {
        setIsSubmitting(false);
        recaptchaRef.current?.reset();
      });
    }
  };

  const lastPassIgnoreProps = {
    autoComplete: "off",
    "data-lpignore": "true",
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="body">
      <DialogTitle>Course Registration for {selectedCourseName}</DialogTitle>
      <form onSubmit={handleSubmit} data-lpignore="true">
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                name="email"
                label="Department Email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                disabled={!!participantData}
                inputProps={lastPassIgnoreProps}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                name="confirmEmail"
                label="Confirm Email Address"
                type="email"
                value={formData.confirmEmail}
                onChange={handleChange}
                error={!!errors.confirmEmail}
                helperText={errors.confirmEmail}
                disabled={!!participantData}
                inputProps={lastPassIgnoreProps}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                name="firstName"
                label="First Name"
                value={formData.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                inputProps={lastPassIgnoreProps}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                inputProps={lastPassIgnoreProps}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                name="positionTitle"
                label="Position/Title"
                value={formData.positionTitle}
                onChange={handleChange}
                error={!!errors.positionTitle}
                helperText={errors.positionTitle}
                inputProps={lastPassIgnoreProps}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                name="organization"
                label="Organization"
                value={formData.organization}
                onChange={handleChange}
                error={!!errors.organization}
                helperText={errors.organization}
                inputProps={lastPassIgnoreProps}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                name="address"
                label="Organization Address"
                value={formData.address}
                onChange={handleChange}
                error={!!errors.address}
                helperText={
                  errors.address ||
                  "Enter full address including street, city, state, and zip code"
                }
                inputProps={lastPassIgnoreProps}
              />
            </Grid>
            <Grid size={{ xs: 9, sm: 6, md: 3 }}>
              <TextField
                required
                fullWidth
                name="phoneNumber"
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={handleChange}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber || "Format: (123) 456-7890"}
                inputProps={lastPassIgnoreProps}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting || loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting || loading}
          >
            {isSubmitting || loading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RegistrationForm;
